import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ApexFunnelChart from '../../../WingmanIntelligence/widgets/ApexFunnelChart';
import { getPartnerCardSx } from '../../partnerDashboardTheme';
import { buildPartnerEventStatisticsModel } from '../partnerEventStatisticsUtils';
import PartnerEventStatCard from './PartnerEventStatCard';
import { usePortalTokens } from '../../../../styles/usePortalTokens';

const CHART_MIN_HEIGHT = 320;
const TICKET_CHART_HEIGHT = 260;

const PartnerEventStatistics = ({ eventData }) => {
  const theme = useTheme();
  const portal = usePortalTokens();
  const model = useMemo(() => buildPartnerEventStatisticsModel(eventData ?? {}), [eventData]);

  const ticketSalesMax = useMemo(
    () => Math.max(0, ...model.ticketSalesTrend.map((point) => point.sales)),
    [model.ticketSalesTrend],
  );

  const hasTicketSalesData = ticketSalesMax > 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {model.summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.id}>
            <PartnerEventStatCard
              title={card.title}
              value={card.value}
              iconKey={card.iconKey}
              iconBg={card.iconBg}
              iconColor={card.iconColor}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              ...getPartnerCardSx(portal),
              minHeight: CHART_MIN_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ApexFunnelChart title="RSVP Funnel" stages={model.funnelStages} variant="rsvp" />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              ...getPartnerCardSx(portal),
              p: 2,
              minHeight: CHART_MIN_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.01em',
                mb: 2,
              }}
            >
              Ticket Sales Trend
            </Typography>
            <Box sx={{ height: TICKET_CHART_HEIGHT, width: '100%' }}>
              {hasTicketSalesData ? (
                <ResponsiveContainer width="100%" height={TICKET_CHART_HEIGHT}>
                  <LineChart
                    data={model.ticketSalesTrend}
                    margin={{ top: 8, right: 16, left: 4, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={theme.palette.divider}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      allowDecimals={false}
                      domain={[0, Math.max(ticketSalesMax, 1)]}
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <Tooltip
                      formatter={(value) => [value, 'Tickets sold']}
                      contentStyle={{
                        borderRadius: 8,
                        fontSize: 12,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: theme.palette.primary.main, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: theme.palette.primary.main }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 2, textAlign: 'center' }}
                  >
                    No ticket sales data yet
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerEventStatistics;
